
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileJson } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { validateImportJson, processBatchWithEmbeddings } from '../utils/knowledge-import-export';
import { useAiManagement } from '@/hooks/use-ai-management';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ImportKnowledgeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

export const ImportKnowledgeDialog: React.FC<ImportKnowledgeDialogProps> = ({
  open,
  onOpenChange,
  onImportComplete
}) => {
  const [jsonData, setJsonData] = useState('');
  const [importMethod, setImportMethod] = useState<'upload' | 'paste'>('upload');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [validationError, setValidationError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { generateEmbedding } = useAiManagement();

  const resetState = () => {
    setJsonData('');
    setProgress(0);
    setProgressText('');
    setValidationError('');
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError('');
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/json') {
      setValidationError('Only JSON files are supported');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const fileContent = event.target?.result as string;
        setJsonData(fileContent);
      } catch (error) {
        setValidationError('Failed to read file');
        console.error('Error reading file:', error);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    try {
      setValidationError('');
      setIsProcessing(true);
      setProgress(0);
      setProgressText('Validating data...');
      
      // Parse and validate JSON
      let parsedData;
      try {
        parsedData = JSON.parse(jsonData);
      } catch (error) {
        throw new Error('Invalid JSON format');
      }
      
      const validation = validateImportJson(parsedData);
      if (!validation.valid) {
        throw new Error(validation.message);
      }
      
      setProgressText(`Starting import of ${parsedData.length} documents...`);
      
      // Process documents with embeddings
      const result = await processBatchWithEmbeddings(
        parsedData,
        generateEmbedding,
        (current, total) => {
          const percentage = Math.round((current / total) * 100);
          setProgress(percentage);
          setProgressText(`Processing document ${current} of ${total} (${percentage}%)`);
        }
      );
      
      // Show results
      if (result.failed > 0) {
        toast.warning(`Import completed with issues: ${result.success} succeeded, ${result.failed} failed`);
        console.error('Import errors:', result.errors);
      } else {
        toast.success(`Successfully imported ${result.success} documents`);
      }
      
      // Close dialog and refresh data
      setTimeout(() => {
        resetState();
        onOpenChange(false);
        onImportComplete();
      }, 1000);
      
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      setValidationError(message);
      toast.error(`Import failed: ${message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!isProcessing) {
        if (!newOpen) {
          resetState();
        }
        onOpenChange(newOpen);
      }
    }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Knowledge Documents</DialogTitle>
        </DialogHeader>
        
        {isProcessing ? (
          <div className="space-y-4 py-4">
            <div className="text-center text-sm">{progressText}</div>
            <Progress value={progress} className="h-2" />
          </div>
        ) : (
          <>
            <Tabs value={importMethod} onValueChange={(value) => setImportMethod(value as 'upload' | 'paste')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="upload">Upload JSON File</TabsTrigger>
                <TabsTrigger value="paste">Paste JSON</TabsTrigger>
              </TabsList>
              
              <TabsContent value="upload" className="py-4">
                <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-md p-10 bg-muted/20">
                  <Upload size={40} className="text-muted-foreground mb-4" />
                  <p className="mb-2 text-sm font-medium">Click to upload or drag and drop</p>
                  <p className="text-xs text-muted-foreground mb-4">JSON files only</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileChange}
                    className="hidden"
                    id="json-file-upload"
                  />
                  <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                    <FileJson className="mr-2 h-4 w-4" />
                    Select JSON File
                  </Button>
                  {jsonData && (
                    <p className="text-xs text-muted-foreground mt-4">
                      File loaded. Ready to import.
                    </p>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="paste" className="py-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Paste your JSON data below. Make sure it's properly formatted.
                </p>
                <Textarea
                  value={jsonData}
                  onChange={(e) => {
                    setJsonData(e.target.value);
                    setValidationError('');
                  }}
                  placeholder='[{"title": "Document Title", "document": {"content": "Document content"}, "metadata": {"language": "en", "topic": "guide", "tags": ["tag1", "tag2"]}}]'
                  className="min-h-[200px] font-mono text-sm"
                />
              </TabsContent>
            </Tabs>
            
            {validationError && (
              <Alert variant="destructive" className="mt-2">
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}
            
            <div className="bg-muted/30 p-4 rounded-md mt-4">
              <h4 className="text-sm font-medium mb-2">JSON Format Example:</h4>
              <pre className="text-xs overflow-x-auto p-2 bg-background rounded border whitespace-pre-wrap">
{`[
  {
    "title": "Document Title",
    "document": {
      "content": "Document content goes here..."
    },
    "metadata": {
      "language": "en",
      "topic": "guide",
      "tags": ["tag1", "tag2"],
      "is_multi_language": false
    }
  }
]`}
              </pre>
            </div>
          </>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!jsonData || isProcessing || !!validationError}>
            {isProcessing ? 'Importing...' : 'Import Documents'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
